<template>
  <div id="item-list" class="mt-6">
    <hr v-if="index !== 0" class="mt-0 mb-6" />

    <div class="row py-2">
      <div class="col">
        <img
          v-if="cartBuyback.declaration.product.image"
          :src="cartBuyback.declaration.product.image"
          class="cart-item-img mw-80px mh-80px"
          loading="lazy"
        />
        <inline-svg v-else class="cart-item-img mw-80px mh-80px" src="/media/cirx/cx-no-image-found.svg" />
      </div>

      <div class="col align-self-center text-right">
        <button
          class="btn p-0 cirx-table-action-btn"
          @click="removeItem(cartBuyback.inCartId, cartBuyback.declaration.product.label)"
        >
          <inline-svg class="cirx-custom-primary-color-svg" src="/media/cirx/trash_icon.svg" />
        </button>
      </div>
    </div>

    <div class="row py-2">
      <div class="col">
        <span class="cirx-custom-primary-color">
          {{ cartBuyback.declaration.product.brand }}
        </span>
        <br />
        <span>{{ cartBuyback.declaration.product.label }}</span>
      </div>

      <div class="col">
        <span class="cirx-custom-primary-color font-weight-bold float-right">
          {{ localizeCurrency(cartBuyback.estimatedPrice) }}
        </span>
      </div>
    </div>

    <table class="table table-borderless mb-0">
      <tr v-if="cartBuyback.bonus !== null">
        <td class="cirx-text-muted px-0">{{ translate("BONUS.LABEL") }}</td>
        <td class="cirx-custom-primary-color text-right pr-0">
          +{{ localizeCurrency(cartBuyback.bonus.amountInCents / 100) }}
        </td>
      </tr>
      <tr v-for="(impactPrice, indexImpactPrice) in cartBuyback.impactPriceEntries" :key="indexImpactPrice">
        <td class="cirx-text-muted px-0">{{ impactPrice.label }}</td>
        <td class="cirx-custom-primary-color text-right pr-0">{{ impactPrice.value }}</td>
      </tr>
    </table>
  </div>
</template>

<script setup lang="ts">
import { emit } from "@/core/services/eventBus";
import { EVENTS } from "@/core/constants/events.js";
import { routes } from "@/routing/routes.js";
import { storeToRefs } from "pinia";
import { useCartStore } from "@/core/store/cart/cartStore";
import useModalStore from "@/core/store/layout/modalStore";
import useTranslate from "@/core/composables/translate";
import { defineProps } from "vue";
import { BuybackInCart } from "@/apps/shared/buyback/core/domain/model/buybackInCart/BuybackInCart";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();
const { setModal } = useModalStore();
const { translate, localizeCurrency } = useTranslate();
const { cart } = storeToRefs(useCartStore());
const { removeBuybackFromCart } = useCartStore();

const { cartBuyback, index } = defineProps({
  cartBuyback: {
    type: BuybackInCart,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
});

const removeItem = (passedCartBuybackId: string, itemLabel: string): void => {
  const thisIsTheLastItem = cart.value.cartBuybacks.length < 2;

  const contentText = thisIsTheLastItem
    ? translate("CART.REMOVE_MODAL.CONTENT", {
        item: itemLabel,
      })
    : itemLabel;

  const modalTexts = {
    TITLE: translate("CART.REMOVE_MODAL.TITLE"),
    CONTENT: contentText,
    CANCEL_LABEL: translate("CART.REMOVE_MODAL.CANCEL_LABEL"),
    CONFIRM_LABEL: translate("CART.REMOVE_MODAL.CONFIRM_LABEL"),
  };

  const confirmFunction = () => {
    removeBuybackFromCart(passedCartBuybackId);

    if (thisIsTheLastItem && routes.home.name !== route.name) {
      emit(EVENTS.buybackCreationWizard.disableNavigationWarningEvent);

      router.push(routes.home.path);
    }
  };

  setModal({
    texts: modalTexts,
    functions: {
      confirmFunction: confirmFunction,
    },
  });
};
</script>

<style scoped lang="scss">
.cart-item-img {
  object-fit: contain;
}
</style>
